import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { User } from 'firebase/auth'
import { auth } from './config/firebase'

import HomePage from './pages/HomePage.tsx'
import GamePage from './pages/GamePage.tsx'
import OceanDashPage from './pages/OceanDashPage.tsx'
import PollutionPatrolPage from './pages/PollutionPatrolPage.tsx'
import PearlPopPage from './pages/PearlPopPage.tsx'
import AboutPage from './pages/AboutPage.tsx'
import AuthPage from './pages/AuthPage.tsx'
import ProfilePage from './pages/ProfilePage.tsx'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.tsx'
import CookiePolicyPage from './pages/CookiePolicyPage.tsx'
import TermsPage from './pages/TermsPage.tsx'
import OceanPollutionPage from './pages/OceanPollutionPage.tsx'
import ParentDashboardPage from './pages/ParentDashboardPage.tsx'
import SubscriptionPage from './pages/SubscriptionPage.tsx'
import HelpingTheEnvironmentPage from './pages/HelpingTheEnvironmentPage.tsx'
import CharacterCreationPage from './pages/CharacterCreationPage.tsx'
import ChildProfilePage from './pages/ChildProfilePage.tsx'

// Game Info Pages
import SimpleMatchInfoPage from './pages/SimpleMatchInfoPage.tsx'
import OceanDashInfoPage from './pages/OceanDashInfoPage.tsx'
import PollutionPatrolInfoPage from './pages/PollutionPatrolInfoPage.tsx'
import PearlPopInfoPage from './pages/PearlPopInfoPage.tsx'
import CharacterCreatorInfoPage from './pages/CharacterCreatorInfoPage.tsx'

import Header from './components/Header'
import Footer from './components/Footer'
import CookieConsentBanner from './components/CookieConsentBanner'
import ScrollToTop from './components/ScrollToTop'
import { ParallaxLayout } from './components/layout/ParallaxLayout'

import { ChildProvider } from './context/ChildContext'
import { AnalyticsConsentManager } from './providers/PostHogProvider'
import { trackPageView } from './services/analyticsService'
import { Toaster } from 'sonner'

// Component to track page views
function PageViewTracker() {
  const location = useLocation()

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname)
  }, [location.pathname])

  return null
}

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <AnalyticsConsentManager user={user}>
      <ChildProvider>
        <Router>
          <ScrollToTop />
          <PageViewTracker />
          <ParallaxLayout>
            <Header user={user} />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<HomePage user={user} />} />
                
                {/* Simple Match - Info and Play */}
                <Route path="/simple-match" element={<SimpleMatchInfoPage />} />
                <Route path="/simple-match/play" element={<GamePage />} />
                <Route path="/game" element={<GamePage />} /> {/* Legacy route */}
                
                {/* Ocean Dash - Info and Play */}
                <Route path="/ocean-dash" element={<OceanDashInfoPage />} />
                <Route path="/ocean-dash/play" element={<OceanDashPage />} />
                
                {/* Pollution Patrol - Info and Play */}
                <Route path="/pollution-patrol" element={<PollutionPatrolInfoPage />} />
                <Route path="/pollution-patrol/play" element={<PollutionPatrolPage />} />
                
                {/* Pearl Pop - Info and Play */}
                <Route path="/pearl-pop" element={<PearlPopInfoPage />} />
                <Route path="/pearl-pop/play" element={<PearlPopPage />} />
                
                <Route path="/about" element={<AboutPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/ocean-pollution" element={<OceanPollutionPage />} />
                <Route path="/parent-dashboard" element={<ParentDashboardPage />} />
                <Route path="/subscription" element={<SubscriptionPage />} />
                <Route path="/helping-the-environment" element={<HelpingTheEnvironmentPage />} />
                
                {/* Character Creator - Info and Create */}
                <Route path="/character-creator" element={<CharacterCreatorInfoPage />} />
                <Route path="/character-creation" element={<CharacterCreationPage />} />
                
                <Route path="/child-profile" element={<ChildProfilePage />} />
              </Routes>
            </main>
            <Footer />
            <CookieConsentBanner user={user} />
            <Toaster />
          </ParallaxLayout>
        </Router>
      </ChildProvider>
    </AnalyticsConsentManager>
  )
}

export default App
